import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { Mediator } from '@spotahome/soyuz-mediator';
import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { lastDayOfMonth, setDate } from 'date-fns';

import { formatReadableDateRange } from '../../../utils/DateRangeHandler';
import { getRentalTypesDisplay } from '../../../utils/rentalType';
import {
  formatDate,
  addDaysToDate,
  subtractDaysToDate
} from '../../../utils/dateUtils';
import { CHANNELS } from '../../../utils/constants';
import { useMediaQuery } from '../../../utils/breakpoints/breakpoints';

import FlexibleOptions, {
  THREE_WEEKS_OFFSET
} from '../../../Filters/AvailabilityFilter/FlexibleOptions';
import DateRangeFilter from '../../../Filters/DateRangeFilter';
import MonthlyFilter from '../../../Filters/MonthlyFilter';

import ExposedFilter from '../ExposedFilter';

const noop = () => {};

const AvailabilityFilter = ({
  moveIn,
  moveOut,
  selectedRentalTypes,
  isFilterOpen,
  onToggleFilter,
  onFiltersUpdate,
  minimumStaying,
  totalResults,
  moveOutFrom = '',
  moveOutTo = '',
  moveInFrom = '',
  moveInTo = '',
  showResults,
  onShowResultsClick = noop
}) => {
  const [dateRangeOffset, setDateRangeOffset] = useState(THREE_WEEKS_OFFSET);
  const [isMonthlyFilter, setIsMonthlyFilter] = useState(false);
  const { isBiggerThanTablet } = useMediaQuery();

  const renderButtonContent = () => {
    const labelContent = [];
    if (moveOut || (moveOutFrom && moveOutTo) || moveIn) {
      labelContent.push(
        formatReadableDateRange(
          moveIn || moveInFrom,
          moveOut || moveOutTo,
          isMonthlyFilter
        )
      );
    }

    if (selectedRentalTypes?.length) {
      labelContent.push(getRentalTypesDisplay(selectedRentalTypes));
    }

    if (labelContent.length) {
      return labelContent.join(' - ');
    }

    return trans('filters.label.availabilities');
  };

  const handleFlexibleOptionsChange = value => {
    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      section: 'move-in-flexible-options',
      value
    });
    setDateRangeOffset(value);
  };

  const handleDateRangeChange = ({
    moveIn: newMoveIn,
    moveOut: newMoveOut
  }) => {
    const change = {};
    if (newMoveIn) {
      change['move-in'] = newMoveIn;
      change.moveInFrom = formatDate(
        subtractDaysToDate(newMoveIn, dateRangeOffset)
      );
      change.moveInTo = formatDate(addDaysToDate(newMoveIn, dateRangeOffset));
    }

    if (newMoveOut) {
      change['move-out'] = newMoveOut;
      change.moveOutFrom = formatDate(
        subtractDaysToDate(newMoveOut, dateRangeOffset)
      );
      change.moveOutTo = formatDate(addDaysToDate(newMoveOut, dateRangeOffset));
    }

    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      action: 'apply',
      method: 'dates'
    });

    onFiltersUpdate(change);
  };

  const handleMonthlyFilterChange = ({
    moveIn: newMoveIn,
    moveOut: newMoveOut
  }) => {
    const change = {};
    if (newMoveIn) {
      change['move-in'] = formatDate(setDate(newMoveIn, 15));
      change.moveInFrom = formatDate(setDate(newMoveIn, 1));
      change.moveInTo = formatDate(lastDayOfMonth(newMoveIn));
    }

    if (newMoveOut) {
      change['move-out'] = formatDate(setDate(newMoveOut, 15));
      change.moveOutFrom = formatDate(setDate(newMoveOut, 1));
      change.moveOutTo = formatDate(lastDayOfMonth(newMoveOut));
    }

    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      action: 'apply',
      method: 'months'
    });

    onFiltersUpdate(change);
  };

  const handleClearFilter = e => {
    e.preventDefault();
    e.stopPropagation();

    Mediator.publish(CHANNELS.REFRESH_CALENDAR_DATEPICKER);

    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      action: 'clear'
    });

    onFiltersUpdate({
      includeBlockedProperties: 0,
      'move-in': null,
      'move-out': null,
      moveInFrom: null,
      moveInTo: null,
      moveOutFrom: null,
      moveOutTo: null
    });
  };

  const handleFilterSwitcherClick = e => {
    e?.preventDefault();
    e?.stopPropagation();

    setTimeout(() => {
      setIsMonthlyFilter(!isMonthlyFilter);
    }, 0);
  };

  useEffect(() => {
    if (!isMonthlyFilter) {
      handleDateRangeChange({
        moveIn,
        moveOut
      });
    }
  }, [dateRangeOffset]);

  useEffect(() => {
    if (isFilterOpen) {
      SoyuzAnalytics.sendGA4Event('filter', {
        context: 'availabilities',
        section: 'switcher',
        value: isMonthlyFilter ? 'months' : 'dates'
      });
    }
  }, [isMonthlyFilter, isFilterOpen]);

  return (
    <ExposedFilter
      roundedCorners
      isFilterOpen={isFilterOpen}
      onToggleFilter={onToggleFilter}
      renderButtonContent={renderButtonContent}
      keepOpen={isMonthlyFilter}
      renderFilterContent={() =>
        isMonthlyFilter ? (
          <MonthlyFilter
            from={moveIn}
            to={moveOut}
            minMonthStay={Math.floor(minimumStaying / 30)}
            onDatesChange={handleMonthlyFilterChange}
            onFilterSwitcherClick={handleFilterSwitcherClick}
          />
        ) : (
          <DateRangeFilter
            from={moveIn}
            minStay={minimumStaying}
            onDatesChange={handleDateRangeChange}
            onFlexibleOptionsChange={handleFlexibleOptionsChange}
            to={moveOut}
            dateRangeOffset={dateRangeOffset}
            initiallyOpen
            keepMoveOutCalendarOpen
            hideInput={isBiggerThanTablet()}
            onFilterSwitcherClick={handleFilterSwitcherClick}
          />
        )
      }
      renderFooterContent={
        isMonthlyFilter
          ? () => <div /> // will push buttons to the right
          : () =>
              isBiggerThanTablet() ? (
                <FlexibleOptions
                  onChange={handleFlexibleOptionsChange}
                  value={dateRangeOffset}
                />
              ) : null
      }
      onClearFilter={handleClearFilter}
      totalResults={totalResults}
      showResults={showResults}
      onShowResultsClick={onShowResultsClick}
      filterConfig={{
        name: 'availabilities',
        title: '',
        subtitle: '',
        eventLabel: 'apply-date',
        dataTestOpen: 'date-exposed-filter-button',
        dataTestApply: 'date-exposed-filter-apply',
        clearText: 'search.exposedfilters.dates.clear',
        isSelected:
          !!(moveIn || moveOut) ||
          !!selectedRentalTypes?.length ||
          !!(moveInFrom || moveInTo)
      }}
    />
  );
};

AvailabilityFilter.propTypes = {
  moveIn: PropTypes.string.isRequired,
  moveOut: PropTypes.string.isRequired,
  selectedRentalTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  isFilterOpen: PropTypes.bool.isRequired,
  onToggleFilter: PropTypes.func.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  totalResults: PropTypes.number.isRequired,
  moveOutFrom: PropTypes.string,
  moveOutTo: PropTypes.string,
  moveInFrom: PropTypes.string,
  moveInTo: PropTypes.string,
  showResults: PropTypes.bool.isRequired,
  onShowResultsClick: PropTypes.func
};

export default AvailabilityFilter;
